import { CONFIG } from "../../appConfig";
import { get } from "../../utils/js/object";
import { types } from "../../utils/js/types";
import STATE from "../../constants/state";

const PROFILE = "profile/";

export default {
  /**
   * @desc Create a profile with supplied loyalty type; i.e. 'nl', 'oci'
   * @param {Object} data {endpoint, params, payload}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * const data = {payload: {...}};
   * data.endpoint = {loyalty:'oci'}; // will store created profile in `state.gmi.session.gma.oci_profile`
   * data.endpoint = {loyalty:'nl'}; // will store created profile in `state.gmi.session.gbo.profile`
   * services.gma.profile.createProfile(data);
   */
  createProfile(data = {}) {
    const { endpoint = { loyalty: "" }, params = {}, payload = {} } = data;
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}createProfile${
        endpoint.loyalty ? "/" + endpoint.loyalty : ""
      }`,
      data: payload,
      params,
    };
  },

  /**
   * @desc Fetches an NL profile object attached to the passed in individual id
   * @param {Object} data {endpoint, params}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * const data = {
   *  endpoint: {
   *    individual_id, // *(String)*
   *  }
   * }
   * services.gma.profile.getNLProfile(data);
   */
  getNLProfile(data = {}) {
    const { endpoint = {}, params = {} } = data;
    const { individual_id = "" } = endpoint;
    return {
      method: "GET",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}getNLProfile${
        individual_id ? "/" + individual_id : ""
      }`,
      params,
    };
  },

  /**
   * @desc Login user by userName, password, and remember fields; get profile for session
   * @param {Object} data {payload, params}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * const data = {
   *  payload: {
   *    username, // *(String)*
   *    password, // *(String)*
   *    remember_credentials // *(Boolean)*
   *  }
   * }
   * services.gma.profile.login(data);
   * // dispatched results stored in...
   * // `state.gmi.session.gbo.profile` // gbo profile object
   * // `state.gmi.session.gma.logged_in` // true
   */
  login(data = {}) {
    const { params = {}, payload = {} } = data;
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}login`,
      data: payload,
      params,
    };
  },

  /**
   * @desc Login user with emerald club by userName, password, and remember fields; get profile for session
   * @param {Object} data {payload, params}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * const data = {
   *  params: {
   *    username, // *(String)*
   *    password, // *(String)*
   *    remember_credentials // *(Boolean)*
   *  }
   * }
   * services.gma.profile.loginWithEmeraldClub(data);
   * // dispatched results stored in...
   * // `state.gmi.session.gbo.profile` // gbo profile object
   * // `state.gmi.session.gma.logged_in` // true
   */
  loginWithEmeraldClub(data = {}) {
    const { params = {} } = data;
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}login/EC`,
      data: params,
    };
  },

  /**
   * @desc Login user with enterprise plus by userName, password, and remember fields; get profile for session
   * @param {Object} data {payload, params}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * const data = {
   *  params: {
   *    username, // *(String)*
   *    password, // *(String)*
   *    remember_credentials // *(Boolean)*
   *  }
   * }
   * services.gma.profile.loginWithEnterprisePlus(data);
   * // dispatched results stored in...
   * // `state.gmi.session.gbo.profile` // gbo profile object
   * // `state.gmi.session.gma.logged_in` // true
   */
  loginWithEnterprisePlus(data = {}) {
    const { params = {} } = data;
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}login/EP`,
      data: params,
    };
  },

  /**
   * @desc Attempts to sign in user via service layer WITHOUT credentials
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * services.gma.profile.loginRememberMe();
   * // dispatched results stored in...
   * // `state.gmi.session.gbo.profile` // gbo profile object
   * // `state.gmi.session.gma.logged_in` // true
   */
  loginRememberMe(data = {}) {
    const { params = {} } = data;
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}login/rememberme`,
      params,
    };
  },

  /**
   * @desc Signs out user; calls service, deletes auth cookie
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * services.gma.profile.logout();
   * // dispatched results stored in...
   * // `state.gmi.session.gbo` // no gbo.profile object
   * // `state.gmi.session.gma.logged_in` // false
   */
  logout(data = {}) {
    const { params = {} } = data;
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}logout`,
      params,
    };
  },

  /**
   * @desc Modify the profile of a logged in loyalty user
   * @param {Object} data {payload, params, opts}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * const payload = {
   *   address: {
   *    "address_type": "HOME",
   *    "city": "Chicago",
   *    "country_code": "US",
   *    "country_name": "United States",
   *    "country_subdivision_code": "IL",
   *    "country_subdivision_name": "Illinois",
   *    "postal": "60601",
   *    "street_addresses": [
   *      "300 E Randolph st"
   *     ]
   *   }
   * }
   *
   * const opts = {
   *   mode: {
   *     update:true
   *   }
   * }
   *
   * services.gma.profile.modify({payload, opts});
   */
  modify(data = {}) {
    const { payload = {}, params = {}, opts = {} } = data;
    const additional_info = get(payload, "additional_info") || {};
    const modes = get(opts, "modes");

    if (
      !types(get(additional_info, "update_profile_mode")).isDefined &&
      types(modes).isDefined
    ) {
      if (modes.update === true) {
        additional_info["update_profile_mode"] = "UPDATE";
      }
    }

    payload.additional_info = additional_info;
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}modify`,
      data: payload,
      params,
    };
  },

  /**
   * @desc Fetches the profile information tied to a drivers license.
   * @returns {Object}
   * @memberof services.gma.profile
   * @param {Object} data {endpoint, payload, params}
   * @example
   * const payload = {
   *   country, // *(String)*
   *   driver_license_number, // *(String)*
   *   last_name // *(String)*
   * }
   * services.gma.profile.searchProfileByDriverLicense({payload, endpoint:'is_oci'});
   * if endpoint.is_oci is set to true, profile is also stored in state.gmi.session.gma.online_check_in_profile
   */
  searchProfileByDriverLicense(data = {}) {
    const { payload = {}, params = {}, endpoint = {} } = data;
    const oci = endpoint.is_oci ? "/oci" : "";
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}searchProfileByDriverLicense${oci}`,
      data: payload,
      params,
    };
  },

  /**
   * @desc Fetches the profile information of a loyalty member
   * @param {Object} data {endpoint, payload, params}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * const payload = {
   *   country, // *(String)*
   *   driver_license_number, // *(String)*
   *   last_name // *(String)*
   * }
   * services.gma.profile.searchRenterByMembership({payload: endpoint:'is_oci'});
   * if endpoint.is_oci is set to true, profile is also stored in state.gmi.session.gma.online_check_in_profile
   */
  searchRenterByMembership(data = {}) {
    const { payload = {}, params = {}, endpoint = {} } = data;
    const oci = endpoint.is_oci ? "/oci" : "";
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}searchRenterByMembership${oci}`,
      data: payload,
      params,
    };
  },

  /**
   * @desc Updates profile information.
   * @param {Object} data {endpoint, payload, params}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * const payload = {
   *   "address": {
   *     "address_type": "HOME",
   *     "city": "string",
   *     "country_code": "string",
   *     "country_name": "string",
   *     "country_subdivision_code": "string",
   *     "country_subdivision_name": "string",
   *     "postal": "string",
   *     "street_addresses": [
   *       "string"
   *     ]
   *   }
   * }
   * services.gma.profile.updateBasicProfile({payload, endpoint});
   * `endpoint` can contain a `loyalty` property (i.e. `NL`), and an `individual_id` property to
   * update a specific NL profile with the passed in data
   */
  updateBasicProfile(data = {}) {
    const { payload = {}, params = {}, endpoint = {} } = data;
    const loyalty = endpoint.loyalty ? `/${endpoint.loyalty}` : "";
    const individual_id = endpoint.individual_id
      ? `/${endpoint.individual_id}`
      : "";
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}updateBasicProfile${loyalty}${individual_id}`,
      data: payload,
      params,
    };
  },

  /**
   * @desc Add a payment method to profile.
   * @param {Object} data {payload, params}
   * @param {Object} data.payment_method The payment method object
   * @param {String} data.payment_method.card_holder_name Card holder name
   * @param {Boolean} data.payment_method.expiration_date Card expiration date
   * @param {String} data.payment_method.first_six First six digits of the credit card number
   * @param {String} data.payment_method.last_four Last four digits of the credit card number
   * @param {Boolean} data.payment_method.preferred True if the payment should be set as primary credit card, false otherwise
   * @param {String} data.update_profile_mode The update profile mode to let GMA know its an update
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * const payload = {
   *   payment_method: {
   *     card_holder_name: "Holder Name"
   *     expiration_date: "2042-04",
   *     first_six: "411111",
   *     last_four: "1111",
   *     preferred: "true"
   *   },
   *   update_profile_mode: "UPDATE"
   * }
   * services.gma.profile.addPayment({payload});
   */
  addPayment(data = {}) {
    const { payload = {}, params = {} } = data;

    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}addPayment`,
      data: payload,
      params,
    };
  },

  /**
   * @desc Remove a payment method from profile
   * @param {Object} data {endpoint, params}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * const data = {
   *   endpoint: {
   *     payment_id, // *(String)*
   *   }
   * }
   * services.gma.profile.removePayment({endpoint});
   */
  removePayment(data = {}) {
    const { endpoint = {}, params = {} } = data;
    const { payment_id = "" } = endpoint;

    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}removePayment${payment_id ? "/" + payment_id : ""}`,
      params,
    };
  },

  /**
   * @desc Starts an IBM hosted wallet session.
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * const data = {
   *   payload: {
   *     success_url, // *(String)*
   *     failure_url, // *(String)*
   *     style_sheet_url, // *(String)*
   *     renter_first_name, // *(String)*
   *     renter_last_name // *(String)*
   *   },
   *   params: {}
   * }
   * services.gma.profile.cardSubmissionSession({payload});
   */
  cardSubmissionSession(data) {
    const { payload = {}, params = {} } = data;
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}cardSubmissionSession`,
      data: payload,
      params,
    };
  },

  /**
   * @desc Get payment details object using paymentId
   * @param {Object} data {paymentId}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * services.gma.profile.getPaymentDetailsByPaymentId({ paymentId: ZHLKSDI2454 });
   */
  getPaymentDetailsByPaymentId(data = {}) {
    const { params = {}, paymentId = "" } = data;
    return {
      method: "GET",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}payment${paymentId ? "/" + paymentId : ""}`,
      trimRespProp: "payment_detail",
      params,
    };
  },

  /**
   * @desc Update profile for OCI while in reservation flow and associate profile to the reservation
   * @param {Object} data {payload, params}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * services.gma.profile.updateProfileForCheckin({payload});
   * // dispatched results stored in `state.gmi.session.gma.online_check_in_profile`
   */
  updateProfileForCheckin(data = {}) {
    const { payload = {}, params = {} } = data;
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}updateProfile/oci`,
      data: payload,
      params,
    };
  },

  /**
   * @desc Aborts the online check-in process in case unauthenticated
   *       users look up a profile but need to cancel it
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * services.gma.profile.abortCheckin();
   */
  abortCheckin(data = {}) {
    const { payload = {}, params = {} } = data;
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}abort/oci`,
      data: payload,
      params,
    };
  },

  /**
   * @desc Clears the profile currently in GMA session and replaces partial profile found with new empty profile with DL request details
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * services.gma.profile.clearProfile();
   */
  clearProfile(data = {}) {
    const { params = {} } = data;
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}clearProfile/oci`,
      params,
    };
  },

  /**
   * @desc Creates loyalty profile from non loyalty one based on individual_id
   * @param {Object} data {payload, params, payload}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * const data = {payload: {...}};
   * data.endpoint = {loyalty:'EP'};
   * services.gma.profile.cloneProfile(data);
   */
  cloneProfile(data = {}) {
    const { endpoint = { loyalty: "" }, params = {}, payload = {} } = data;
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}clone${
        endpoint.loyalty ? "/" + endpoint.loyalty : ""
      }`,
      data: payload,
      params,
    };
  },

  /**
   * @desc Sends user an email with link to reset password
   * @param {Object} data {params, payload}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * const data = {
   *   payload: {
   *     first_name, // *(String)*
   *     last_name, // *(String)*
   *     email, // *(String)*
   *   },
   * }
   * services.gma.profile.resetPasswordStart(data);
   */
  resetPasswordStart(data = {}) {
    const { params = {}, payload = {} } = data;
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}resetPasswordStart`,
      data: payload,
      params,
    };
  },

  /**
   * @desc Concludes the reset password flow that starts with the call "resetPasswordStart", by giving the new password for the profile
   * @param {Object} data {params, payload}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * const data = {
   *   payload: {
   *     username, // *(String)*
   *     new_password, // *(String)*
   *   },
   * }
   * services.gma.profile.resetPassword(data);
   */
  resetPassword(data = {}) {
    const { params = {}, payload = {} } = data;
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}resetPassword`,
      data: payload,
      params,
    };
  },

  /**
   * @desc Updates user password
   * @param {Object} data {params, payload}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * const data = {
   *   payload: {
   *     new_password, // *(String)*
   *   },
   * }
   * services.gma.profile.updatePassword(data);
   */
  updatePassword(data = {}) {
    const { params = {}, payload = {} } = data;
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}password`,
      data: payload,
      params,
    };
  },

  /**
   * @desc Sends user an email with the username
   * @param {Object} data {params, payload}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * const data = {
   *   payload: {
   *     first_name, // *(String)*
   *     last_name, // *(String)*
   *     email // *(String)*
   *   },
   * }
   * services.gma.profile.forgotUsername(data);
   */
  forgotUsername(data = {}) {
    const { params = {}, payload = {} } = data;
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}forgotUsername`,
      data: payload,
      params,
    };
  },

  /**
   * @desc Get the notification preferences of the authenticated user
   * @param {Object} data {params, payload}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * services.gma.profile.getNotificationPreferences()
   **/
  getNotificationPreferences(data = {}) {
    const { params = {} } = data;
    return {
      method: "GET",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}notification/push/preference`,
      params,
    };
  },

  /**
   * @desc Update the notification preferences of the authenticated user
   * @param {Object} data {params, payload}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * const data = {
   *   payload: {
   *     notification_preferences: [
   *       {
   *         notification_type, // *(String)* i.e. `STRETCH_FREEDAY`, `LATE_RETURN`
   *         enabled: true
   *       },
   *       {
   *         notification_type, // *(String)*
   *         enabled: false
   *       }
   *     ]
   *   },
   * }
   * services.gma.profile.updateNotificationPreferences(data)
   **/
  updateNotificationPreferences(data = {}) {
    const { params = {}, payload = {} } = data;
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}notification/push/preference`,
      data: payload,
      params,
    };
  },

  /**
   * @desc Register the push notification device token/registration id (rid) to authenticated user
   * @param {Object} data {params, payload}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * const data = {
   *   payload: {
   *     locale // *(String)* i.e. `en_US`
   *   },
   *   endpoint: {
   *     rid // *(String)* i.e. `59ccd98d12c5e5831063deb674f48c821d73b1ac057648a5743f8f1b3025ea46`
   *   },
   * }
   * services.gma.profile.registerForPushNotifications(data)
   **/
  registerForPushNotifications(data = {}) {
    const { endpoint = {}, payload = {}, params = {} } = data;
    const { rid = "" } = endpoint;
    return {
      method: "PUT",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}notification/push/registerDevice/${rid}`,
      data: payload,
      params,
    };
  },

  /**
   * @desc GET profile subscriptions for newsletters and alerts
   * @param {Object} data {endpoint, params}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * services.gma.profile.subscriptions()
   * dispatched results stored in `state.gma.communication_preferences`
   **/
  subscriptions(data = {}) {
    const { endpoint = {}, params = {} } = data;
    const { individual_id = "" } = endpoint;

    return {
      method: "GET",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}subscriptions/retrieve/${individual_id}`,
      params,
    };
  },

  /**
   * @desc UPDATE profile communication preferences for newsletters and alerts
   * @param {Object} data {params, payload}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * services.gma.profile.communicationPreferences()
   * dispatched results stored in `state.gma.communication_preferences`
   **/
  communicationPreferences(data = {}) {
    const { payload = {}, params = {} } = data;

    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}communicationPreferences`,
      data: payload,
      params,
    };
  },

  /**
   * @desc UPDATE subscriptions
   * @param {Object} data {params, payload}
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * services.gma.profile.updateSubscriptions()
   * dispatched results stored in `state.gma.communication_preferences`
   **/
  updateSubscriptions(data = {}) {
    const { payload = {}, params = {} } = data;

    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}subscriptions`,
      data: payload,
      params,
    };
  },

  /**
   * @desc Unsubscribe of only one subscription
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * services.gma.profile.subscriptionsUnsubscribe()
   * no body needed BE will get that from deeplink session
   **/
  subscriptionsUnsubscribe() {
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}subscriptions/unsubscribe`,
    };
  },

  /**
   * @desc Manage preferences unauth
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * services.gma.profile.subscriptionsManage()
   * no body needed BE will get that from deeplink session
   **/
  subscriptionsManage() {
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}subscriptions/manage`,
    };
  },

  /**
   * @desc Retrieve subscription data to unsubscribe
   * @param {Object} data {eSubId}
   * @returns {import("../../actions/request").Response}
   * @memberof services.gma.profile
   * @see {@link actions.md#Response}
   * @example
   * const data = {
   *  eSubId, // *(String)*
   * }
   * services.gma.profile.retrieveSubscription()
   *
   * This returns the same as session, but with the non_loyalty_unsubscribe_individual data
   * so it does not need the statePath to be added
   *
   * We use `gma.non_loyalty_unsubscribe_individual.individual.mask_email` to populate Email Input on `unsubscribe.html`
   *
   * Make sure to call this before calling `unsubscribeNonLoyalty` so the BE knows who they need to unsubscribe
   **/
  retrieveSubscription({ eSubId }) {
    return {
      method: "GET",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}subscriptions/retrieve/nonLoyalty/${eSubId}`,
    };
  },

  /**
   * @desc Unsubscribe non loyalty user
   * @returns {Object}
   * @memberof services.gma.profile
   * @example
   * services.gma.profile.unsubscribeNonLoyalty()
   * no body needed BE will get that from individual in session
   **/
  unsubscribeNonLoyalty() {
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${PROFILE}subscriptions/nonLoyalty/unsubscribe`,
    };
  },
};
